import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { faqs } from '../mockData';
import { ArrowRight } from 'lucide-react';

const ResourcesPage = () => {

  return (
    <div className="min-h-screen pt-20">
      {/* Hero Section */}
      <section className="bg-gradient-to-r from-[#003f6d] to-[#004a85] text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="text-5xl font-bold mb-6">Resources & Knowledge Center</h1>
          <div className="w-20 h-1 bg-[#d4af37] mb-6"></div>
          <p className="text-xl text-gray-200 max-w-3xl">
            Access valuable resources, guides, and tools to support your certification journey.
          </p>
        </div>
      </section>

      {/* Downloads Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Free Downloads & Guides
            </h2>
            <div className="w-20 h-1 bg-[#d4af37] mx-auto mb-6"></div>
            <p className="text-xl text-gray-600">
              Essential resources to help you understand and prepare for certification
            </p>
          </div>

          <Card className="border-dashed border-2 border-gray-200 bg-gray-50">
            <CardContent className="p-10 text-center">
              <p className="text-lg font-semibold text-gray-700">Free Downloads & Guides</p>
              <p className="text-gray-500 mt-2">
                This section is intentionally blank and will be updated with new downloadable documents.
              </p>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Public Information Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl font-bold text-gray-900 mb-4">
                Public Certification Policies & Processes
              </h2>
              <div className="w-20 h-1 bg-[#d4af37] mb-6"></div>
              <p className="text-xl text-gray-600 mb-6">
                Review QSC America’s public information on audit stages, certification decisions,
                complaints and appeals, impartiality, certification marks, and management systems
                covered by our certification activities.
              </p>
              <Link to="/certification/public-information">
                <Button className="bg-[#003f6d] hover:bg-[#002a4d] text-white px-8 py-6 text-lg">
                  View Public Certification Information
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </Link>
            </div>
            <Card className="border-t-4 border-[#003f6d]">
              <CardContent className="p-8">
                <h3 className="text-2xl font-bold text-gray-900 mb-4">Included Topics</h3>
                <ul className="space-y-3 text-gray-600">
                  <li>Audit process description from application through recertification</li>
                  <li>Grant, refuse, maintain, renew, suspend, restore, and withdraw certification</li>
                  <li>Rules for use of QSC America certification marks and related claims</li>
                  <li>Complaints, appeals, impartiality, and information available upon request</li>
                </ul>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* FAQs Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Frequently Asked Questions
            </h2>
            <div className="w-20 h-1 bg-[#d4af37] mx-auto mb-6"></div>
            <p className="text-xl text-gray-600">
              Find answers to common questions about ISO certification
            </p>
          </div>

          <Accordion type="single" collapsible className="space-y-4">
            {faqs.map((faq, index) => (
              <AccordionItem
                key={index}
                value={`item-${index}`}
                className="bg-white border border-gray-200 rounded-lg px-6"
              >
                <AccordionTrigger className="text-left font-semibold text-gray-900 hover:text-[#003f6d] py-4">
                  {faq.question}
                </AccordionTrigger>
                <AccordionContent className="text-gray-600 pb-4">
                  {faq.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* Newsletter Section */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold text-gray-900 mb-4">
            Stay Updated with Industry Insights
          </h2>
          <div className="w-20 h-1 bg-[#d4af37] mx-auto mb-6"></div>
          <p className="text-xl text-gray-600 mb-8">
            Subscribe to our newsletter for the latest news, tips, and updates on ISO standards
          </p>
          <div className="flex flex-col sm:flex-row gap-4 max-w-2xl mx-auto">
            <input
              type="email"
              placeholder="Enter your email address"
              className="flex-1 px-6 py-4 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#003f6d]"
            />
            <Button className="bg-[#003f6d] hover:bg-[#002a4d] text-white px-8 py-4">
              Subscribe
            </Button>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-[#003f6d] text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold mb-6">
            Need More Information?
          </h2>
          <p className="text-xl text-gray-200 mb-8">
            Our certification experts are here to answer your questions
          </p>
          <Link to="/contact">
            <Button className="bg-[#d4af37] hover:bg-[#c49b2a] text-[#003f6d] font-semibold px-8 py-6 text-lg">
              Contact Our Team
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default ResourcesPage;
